import { toast } from 'sonner';
import { useState } from 'react';
import {
  Search, Download, MoreVertical, Clock, Ban, CheckCircle, AlertTriangle,
  History, Crown, Calendar, Tag, KeyRound, DollarSign, Trash2, UserX, Archive, RotateCcw, ShieldAlert,
  ArrowUpRight, Copy as CopyIcon, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import UserAvatar from '@/components/UserAvatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';

const TIER_BADGES = {
  free: 'bg-gray-100 text-gray-600',
  gather: 'bg-gray-100 text-gray-600',
  connect: 'bg-gray-100 text-gray-600',
  connected: 'bg-teal text-white',
  premium: 'bg-teal text-white',
  thrive: 'bg-teal text-white',
};
const TIER_LABELS = {
  free: 'Connect', gather: 'Connect', connect: 'Connect',
  connected: 'Thrive', premium: 'Thrive', thrive: 'Thrive',
};
const tierLabel = (tier) => TIER_LABELS[tier] || tier;

const getAccountStatusBadge = (member) => {
  const status = member.account_status || 'active';
  if (status === 'suspended') return { label: 'Suspended', className: 'bg-amber-100 text-amber-800 border-amber-300' };
  if (status === 'disabled') return { label: 'Disabled', className: 'bg-red-100 text-red-800 border-red-300' };
  return { label: 'Active', className: 'bg-green-100 text-green-800 border-green-300' };
};

const getPaymentStatusBadge = (member) => {
  if (member.membership_paused) return { label: 'Paused', className: 'bg-purple-100 text-purple-700 border-purple-300' };
  if (member.promo_status === 'active') return { label: 'Promo', className: 'bg-blue-100 text-blue-700 border-blue-300' };
  const tier = member.membership_tier || 'free';
  if (tier === 'free' || tier === 'gather' || tier === 'connect') return { label: 'Free', className: 'bg-gray-100 text-gray-600 border-gray-200' };
  const ps = member.payment_status;
  if (ps === 'failed') return { label: 'Past Due', className: 'bg-red-100 text-red-700 border-red-300' };
  if (ps === 'cancelled') return { label: 'Cancelled', className: 'bg-amber-100 text-amber-700 border-amber-300' };
  if (ps === 'paused') return { label: 'Paused', className: 'bg-purple-100 text-purple-700 border-purple-300' };
  return { label: 'Active', className: 'bg-green-100 text-green-700 border-green-300' };
};

export default function AdminMembersTab({
  members, cities,
  searchQuery, setSearchQuery,
  tierFilter, setTierFilter,
  cityFilter, setCityFilter,
  zipCodeFilter, setZipCodeFilter,
  paymentFilter, setPaymentFilter,
  handleSearch, handleExportMembers,
  openAccountAction, openStatusHistory,
  handlePauseMembership, handleUnpauseMembership,
  handleUnlinkPayment, handleRelinkPayment,
  setPromoModal, setMemberPromoCode,
  setResetModal, setResetEmailChoice, setResetCustomEmail,
  openDeleteMemberModal, openBulkDeleteModal,
  showingDeleted, toggleDeletedView,
  deletedMembers, deletedLoading,
  handleRestoreMember, handlePurgeDeletedMember, openPurgeAllModal,
  runStripeAudit,
  getAuthHeader, fetchData,
  isMainAdmin = false,
}) {
  // Promo-to-paid dialog state (main-admin only feature)
  const [promoToPaidMember, setPromoToPaidMember] = useState(null);
  const [promoToPaidPriceType, setPromoToPaidPriceType] = useState('founding');
  const [promoToPaidSendEmail, setPromoToPaidSendEmail] = useState(true);
  const [promoToPaidSubmitting, setPromoToPaidSubmitting] = useState(false);
  const [promoToPaidResult, setPromoToPaidResult] = useState(null);

  const openPromoToPaid = (m) => {
    setPromoToPaidMember(m);
    setPromoToPaidPriceType('founding');
    setPromoToPaidSendEmail(true);
    setPromoToPaidResult(null);
  };

  const submitPromoToPaid = async () => {
    if (!promoToPaidMember) return;
    setPromoToPaidSubmitting(true);
    try {
      const res = await api.post(
        `/stripe/admin/members/${promoToPaidMember.id}/promo-to-paid`,
        { price_type: promoToPaidPriceType, send_email: promoToPaidSendEmail },
        { headers: getAuthHeader() },
      );
      setPromoToPaidResult(res.data);
      toast.success(
        res.data.email_sent
          ? `Checkout link generated & emailed to ${promoToPaidMember.email}`
          : 'Checkout link generated — copy and share manually',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate checkout link');
    } finally {
      setPromoToPaidSubmitting(false);
    }
  };

  const copyCheckoutUrl = async () => {
    if (!promoToPaidResult?.checkout_url) return;
    try {
      await navigator.clipboard.writeText(promoToPaidResult.checkout_url);
      toast.success('Checkout link copied to clipboard');
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  if (showingDeleted) {
    return (
      <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="admin-deleted-members">
        <div className="p-4 border-b border-border-subtle flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-navy flex items-center gap-2">
              <Archive size={18} className="text-text-mid" /> Deleted Accounts
            </h3>
            <p className="text-xs text-text-mid">Soft-deleted members. Restore to reactivate or purge to remove permanently.</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={toggleDeletedView}
              variant="outline"
              className="rounded-xl"
              data-testid="back-to-active-btn"
            >
              Back to Active Members
            </Button>
            {deletedMembers.length > 0 && openPurgeAllModal && (
              <Button
                onClick={openPurgeAllModal}
                variant="outline"
                className="rounded-xl border-red-300 text-red-700 hover:bg-red-50"
                data-testid="purge-all-deleted-btn"
              >
                <Trash2 size={16} className="mr-2" /> Purge All ({deletedMembers.length})
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          {deletedLoading ? (
            <p className="text-center text-text-mid py-10">Loading…</p>
          ) : deletedMembers.length === 0 ? (
            <p className="text-center text-text-mid py-10">No deleted accounts.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Deleted At</TableHead>
                  <TableHead>Deleted By</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deletedMembers.map((member) => (
                  <TableRow key={member.id} data-testid={`deleted-row-${member.id}`}>
                    <TableCell className="font-medium">{member.first_name} {member.last_name}</TableCell>
                    <TableCell className="text-sm">{member.email}</TableCell>
                    <TableCell className="text-sm">{member.city || '—'}</TableCell>
                    <TableCell>
                      <Badge className={TIER_BADGES[member.membership_tier]}>{tierLabel(member.membership_tier)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-text-mid">
                      {member.deleted_at ? new Date(member.deleted_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm">{member.deleted_by || '—'}</TableCell>
                    <TableCell className="text-xs text-text-mid max-w-[180px] truncate">
                      {member.deleted_reason || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRestoreMember(member)}
                          className="rounded-full text-green-600 border-green-300 hover:bg-green-50"
                          data-testid={`restore-member-${member.id}`}
                        >
                          <RotateCcw size={14} className="mr-1" /> Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePurgeDeletedMember(member)}
                          className="rounded-full text-red-700 border-red-300 hover:bg-red-50"
                          data-testid={`purge-member-${member.id}`}
                        >
                          <Trash2 size={14} className="mr-1" /> Purge
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="admin-members-content">
      {/* Filters */}
      <div className="p-4 border-b border-border-subtle flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="rounded-xl"
            data-testid="admin-search-input"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-[150px] rounded-xl">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="connect">Connect (Free)</SelectItem>
            <SelectItem value="thrive">Thrive (Paid)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="w-[180px] rounded-xl">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            {cities.map((city) => (
              <SelectItem key={city} value={city}>{city}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter by zip code"
          value={zipCodeFilter}
          onChange={(e) => setZipCodeFilter(e.target.value.replace(/\D/g, '').slice(0, 5))}
          className="w-[150px] rounded-xl"
          data-testid="admin-zip-filter"
        />
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-[160px] rounded-xl" data-testid="payment-filter">
            <SelectValue placeholder="Payment Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Payments</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="failed">Past Due</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="promo">Promo</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleSearch} className="bg-teal hover:bg-teal-deep rounded-xl">
          <Search size={18} className="mr-2" /> Search
        </Button>
        <Button onClick={handleExportMembers} variant="outline" className="rounded-xl" data-testid="export-members-btn">
          <Download size={18} className="mr-2" /> Export CSV
        </Button>
        {openBulkDeleteModal && (
          <Button
            onClick={openBulkDeleteModal}
            variant="outline"
            className="rounded-xl border-red-300 text-red-600 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <UserX size={18} className="mr-2" /> Bulk Delete
          </Button>
        )}
        {toggleDeletedView && (
          <Button
            onClick={toggleDeletedView}
            variant="outline"
            className="rounded-xl border-gray-300 text-text-mid hover:bg-gray-50"
            data-testid="view-deleted-btn"
          >
            <Archive size={18} className="mr-2" /> View Deleted
          </Button>
        )}
        {runStripeAudit && (
          <Button
            onClick={runStripeAudit}
            variant="outline"
            className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="stripe-audit-btn"
          >
            <ShieldAlert size={18} className="mr-2" /> Audit Stripe
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Zip Code</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Billing ID</TableHead>
              <TableHead>Account Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => {
              const statusBadge = getAccountStatusBadge(member);
              const paymentBadge = getPaymentStatusBadge(member);
              return (
                <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                  <TableCell className="py-2">
                    <UserAvatar user={member} size="w-9 h-9" />
                  </TableCell>
                  <TableCell className="font-medium">{member.first_name} {member.last_name}</TableCell>
                  <TableCell className="text-sm">{member.email}</TableCell>
                  <TableCell>{member.city}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">
                      {member.zip_code || '-'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge className={TIER_BADGES[member.membership_tier]}>
                        {tierLabel(member.membership_tier)}
                      </Badge>
                      {member.can_manage_events && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`event-mgr-badge-${member.id}`}>
                          Event Mgr
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={paymentBadge.className}
                      data-testid={`member-payment-${member.id}`}
                    >
                      {paymentBadge.label}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`member-billing-${member.id}`}>
                    {member.has_stripe_id ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Linked</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Missing</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={statusBadge.className}
                      data-testid={`member-status-${member.id}`}
                    >
                      {statusBadge.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{new Date(member.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`member-actions-${member.id}`}>
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(member.account_status || 'active') === 'active' && (
                          <>
                            <DropdownMenuItem
                              onClick={() => openAccountAction('suspend', member)}
                              className="text-amber-600"
                              data-testid={`suspend-member-${member.id}`}
                            >
                              <Clock size={14} className="mr-2" /> Suspend Account
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openAccountAction('disable', member)}
                              className="text-red-600"
                              data-testid={`disable-member-${member.id}`}
                            >
                              <Ban size={14} className="mr-2" /> Disable Account
                            </DropdownMenuItem>
                          </>
                        )}
                        {(member.account_status === 'suspended' || member.account_status === 'disabled') && (
                          <DropdownMenuItem
                            onClick={() => openAccountAction('enable', member)}
                            className="text-green-600"
                            data-testid={`enable-member-${member.id}`}
                          >
                            <CheckCircle size={14} className="mr-2" /> Re-enable Account
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => openAccountAction('warn', member)}
                          data-testid={`warn-member-${member.id}`}
                        >
                          <AlertTriangle size={14} className="mr-2" /> Send Warning Email
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openStatusHistory(member)}
                          data-testid={`history-member-${member.id}`}
                        >
                          <History size={14} className="mr-2" /> View Status History
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-text-light">Change Tier</DropdownMenuLabel>
                        {isMainAdmin && member.promo_status === 'active' && (
                          <DropdownMenuItem
                            data-testid={`promo-to-paid-${member.id}`}
                            onClick={() => openPromoToPaid(member)}
                            className="text-teal-deep"
                          >
                            <ArrowUpRight size={14} className="mr-2" /> Move Promo → Paid Thrive…
                          </DropdownMenuItem>
                        )}
                        {[
                          { value: 'connect', label: 'Connect (Free)' },
                          { value: 'thrive', label: 'Thrive (Paid)' },
                        ].filter((t) => t.value !== member.membership_tier).map((t) => (
                          <DropdownMenuItem
                            key={t.value}
                            data-testid={`change-tier-${member.id}-${t.value}`}
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/members/${member.id}/tier`, { tier: t.value }, { headers: getAuthHeader() });
                                toast.success(`Changed ${member.first_name}'s tier to ${t.label}`);
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to change tier');
                              }
                            }}
                          >
                            <Crown size={14} className="mr-2" /> {t.label}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`toggle-event-access-${member.id}`}
                          onClick={async () => {
                            const newVal = !member.can_manage_events;
                            try {
                              await api.patch(`/admin/members/${member.id}/event-access`, { can_manage_events: newVal }, { headers: getAuthHeader() });
                              toast.success(newVal ? `${member.first_name} can now manage events` : `Event access revoked from ${member.first_name}`);
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to update');
                            }
                          }}
                        >
                          <Calendar size={14} className="mr-2" />
                          {member.can_manage_events ? 'Revoke Event Access' : 'Grant Event Manager'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {!member.membership_paused ? (
                          <DropdownMenuItem
                            onClick={() => handlePauseMembership(member)}
                            className="text-purple-600"
                            data-testid={`pause-member-${member.id}`}
                          >
                            <Clock size={14} className="mr-2" /> Pause Membership
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleUnpauseMembership(member)}
                            className="text-green-600"
                            data-testid={`unpause-member-${member.id}`}
                          >
                            <CheckCircle size={14} className="mr-2" /> Resume Membership
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => { setPromoModal({ open: true, member }); setMemberPromoCode(''); }}
                          data-testid={`apply-promo-${member.id}`}
                        >
                          <Tag size={14} className="mr-2" /> Apply Promo Code
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setResetModal({ open: true, member });
                            setResetEmailChoice('original');
                            setResetCustomEmail('');
                          }}
                          data-testid={`send-reset-${member.id}`}
                        >
                          <KeyRound size={14} className="mr-2" /> Send Password Reset
                        </DropdownMenuItem>
                        {member.has_stripe_id && (
                          <DropdownMenuItem
                            onClick={() => handleUnlinkPayment(member.id, member.email)}
                            className="text-red-600"
                            data-testid={`unlink-payment-${member.id}`}
                          >
                            <DollarSign size={14} className="mr-2" /> Unlink Payment
                          </DropdownMenuItem>
                        )}
                        {member.payment_status === 'cancelled' && member.payment_unlinked_by_admin && (
                          <DropdownMenuItem
                            onClick={() => handleRelinkPayment(member.id, member.email)}
                            className="text-green-600"
                            data-testid={`relink-payment-${member.id}`}
                          >
                            <DollarSign size={14} className="mr-2" /> Relink Payment
                          </DropdownMenuItem>
                        )}
                        {openDeleteMemberModal && !member.is_super_admin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openDeleteMemberModal(member)}
                              className="text-red-700 font-semibold"
                              data-testid={`delete-member-${member.id}`}
                            >
                              <Trash2 size={14} className="mr-2" /> Delete Profile
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Promo → Paid dialog (main admin only) */}
      <Dialog
        open={Boolean(promoToPaidMember)}
        onOpenChange={(v) => { if (!v) { setPromoToPaidMember(null); setPromoToPaidResult(null); } }}
      >
        <DialogContent className="max-w-md" data-testid="promo-to-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              Move {promoToPaidMember?.first_name || 'member'} from Promo to Paid Thrive
            </DialogTitle>
            <DialogDescription>
              Generate a personal Stripe Checkout link at the rate of your choice. When they pay, the
              webhook flips their tier to Thrive and ends the promo automatically.
            </DialogDescription>
          </DialogHeader>

          {!promoToPaidResult ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-text-mid mb-2">Choose rate</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPromoToPaidPriceType('founding')}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${
                      promoToPaidPriceType === 'founding'
                        ? 'border-teal bg-teal/10'
                        : 'border-border-subtle hover:bg-cream/30'
                    }`}
                    data-testid="select-founding-rate"
                  >
                    <div className="text-sm font-semibold text-navy">$24.99 / mo</div>
                    <div className="text-[11px] text-text-mid">Founding rate</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPromoToPaidPriceType('regular')}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${
                      promoToPaidPriceType === 'regular'
                        ? 'border-teal bg-teal/10'
                        : 'border-border-subtle hover:bg-cream/30'
                    }`}
                    data-testid="select-regular-rate"
                  >
                    <div className="text-sm font-semibold text-navy">$49.99 / mo</div>
                    <div className="text-[11px] text-text-mid">Regular rate</div>
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-text-mid cursor-pointer">
                <input
                  type="checkbox"
                  checked={promoToPaidSendEmail}
                  onChange={(e) => setPromoToPaidSendEmail(e.target.checked)}
                  className="rounded"
                  data-testid="send-email-checkbox"
                />
                Email the upgrade link to <span className="font-mono text-xs">{promoToPaidMember?.email}</span>
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-teal/5 border border-teal/20 rounded-2xl p-4">
                <p className="text-sm text-navy font-semibold mb-1">
                  Checkout link ready ({promoToPaidResult.rate_label})
                </p>
                <p className="text-xs text-text-mid mb-3">
                  {promoToPaidResult.email_sent
                    ? `Emailed to ${promoToPaidMember?.email}.`
                    : 'Email was not sent — copy the link below and share manually.'}
                </p>
                <div className="bg-white rounded-lg p-2 text-[11px] font-mono break-all border border-border-subtle max-h-24 overflow-auto" data-testid="promo-to-paid-url">
                  {promoToPaidResult.checkout_url}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyCheckoutUrl}
                  className="mt-2 rounded-full"
                  data-testid="copy-checkout-url-btn"
                >
                  <CopyIcon size={12} className="mr-1" /> Copy link
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            {!promoToPaidResult ? (
              <>
                <Button variant="ghost" onClick={() => setPromoToPaidMember(null)}>Cancel</Button>
                <Button
                  onClick={submitPromoToPaid}
                  disabled={promoToPaidSubmitting}
                  className="bg-teal hover:bg-teal-deep text-white rounded-full"
                  data-testid="submit-promo-to-paid-btn"
                >
                  {promoToPaidSubmitting && <Loader2 size={14} className="animate-spin mr-1" />}
                  Generate Checkout Link
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { setPromoToPaidMember(null); setPromoToPaidResult(null); fetchData?.(); }}
                className="bg-teal hover:bg-teal-deep text-white rounded-full"
                data-testid="close-promo-to-paid-btn"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
